import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircle2Icon, AlertCircleIcon, MailIcon, MessageCircleIcon, ClockIcon } from 'lucide-react';
const BLUE = '#2563EB';
const TEAL = '#0F9E8E';
// Formspree endpoint — shared with the site contact form.
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xkoyvnke';
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 22
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const highlights = [{
  icon: MessageCircleIcon,
  title: 'Product questions',
  text: 'Not sure if RD Ready fits your exam? Ask away.'
}, {
  icon: MailIcon,
  title: 'Account & access',
  text: 'Help with study access, sign-in, or your membership.'
}, {
  icon: ClockIcon,
  title: 'Quick replies',
  text: 'We aim to respond to every message within 48 hours.'
}];
export function RDReadyContact() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    if (formData.get('_gotcha')) {
      setStatus('success');
      return;
    }
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data?.error || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };
  const inputClass = 'w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3.5 text-sm text-[#0F172A] placeholder:text-[#94A3B8] transition-colors focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20';
  const labelClass = 'mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#475569]';
  return <div className="w-full overflow-hidden bg-white text-[#1E293B]" style={{
    fontFamily: '"Poppins", "Inter", system-ui, sans-serif'
  }} data-id="element-2461">
      {/* HERO */}
      <section className="px-5 pt-14 pb-8 text-center md:px-8 md:pt-20" data-id="element-2462">
        <motion.div initial="hidden" animate="visible" variants={{
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} className="mx-auto max-w-3xl" data-id="element-2463">
          <motion.p variants={fadeUp} transition={{
          duration: 0.5
        }} className="text-xs font-bold uppercase tracking-[0.22em]" style={{
          color: TEAL
        }} data-id="element-2464">
            Contact
          </motion.p>
          <motion.h1 variants={fadeUp} transition={{
          duration: 0.5
        }} className="mt-4 text-4xl font-extrabold leading-[1.1] tracking-tight text-[#0F172A] sm:text-5xl" data-id="element-2465">
            Let&apos;s help you get{' '}
            <span style={{
            color: BLUE
          }} data-id="element-2466">
              exam ready.
            </span>
          </motion.h1>
          <motion.p variants={fadeUp} transition={{
          duration: 0.5
        }} className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#5A6B7B]" data-id="element-2467">
            Have a question about RD Ready, your study access, or which exam to
            prepare for? Send us a note and we&apos;ll get back to you.
          </motion.p>
        </motion.div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="px-5 py-8 md:px-8" data-id="element-2468">
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-3" data-id="element-2469">
          {highlights.map(({
          icon: Icon,
          title,
          text
        }) => <div key={title} className="rounded-2xl bg-[#F7F9FC] p-6 text-center" data-id="element-2470">
              <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full" style={{
            backgroundColor: '#EEF4FF'
          }} data-id="element-2471">
                <Icon className="h-5 w-5" style={{
              color: BLUE
            }} data-id="element-2472" />
              </span>
              <h3 className="mt-4 text-sm font-bold text-[#0F172A]" data-id="element-2473">{title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-[#5A6B7B]" data-id="element-2474">
                {text}
              </p>
            </div>)}
        </div>
      </section>

      {/* FORM */}
      <section className="px-5 py-10 md:px-8 md:py-14" data-id="element-2475">
        <div className="mx-auto max-w-3xl rounded-3xl bg-[#F7F9FC] p-6 md:p-10" data-id="element-2476">
          {status === 'success' ? <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} className="rounded-2xl bg-white p-8 text-center shadow-[0_2px_16px_rgba(15,23,42,.05)]" data-id="element-2477">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{
            backgroundColor: '#E1EEE8'
          }} data-id="element-2478">
                <CheckCircle2Icon className="h-6 w-6" style={{
              color: TEAL
            }} data-id="element-2479" />
              </span>
              <h2 className="mt-5 text-2xl font-bold text-[#0F172A]" data-id="element-2480">
                Message sent!
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2481">
                Thanks for reaching out — we&apos;ll get back to you within 48
                hours. Keep an eye on your inbox (and spam folder, just in
                case).
              </p>
              <button type="button" onClick={() => setStatus('idle')} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold transition-transform hover:translate-x-0.5" style={{
            color: BLUE
          }} data-id="element-2482">
                Send another message <ArrowRightIcon className="h-4 w-4" data-id="element-2483" />
              </button>
            </motion.div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2484">
              <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{
            position: 'absolute',
            left: '-9999px',
            width: '1px',
            height: '1px'
          }} data-id="element-2485" />

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-2486">
                <div data-id="element-2487">
                  <label htmlFor="name" className={labelClass} data-id="element-2488">
                    Full name *
                  </label>
                  <input id="name" name="name" type="text" required className={inputClass} placeholder="Your name" data-id="element-2489" />
                </div>
                <div data-id="element-2490">
                  <label htmlFor="email" className={labelClass} data-id="element-2491">
                    Email *
                  </label>
                  <input id="email" name="email" type="email" required className={inputClass} placeholder="you@example.com" data-id="element-2492" />
                </div>
              </div>

              <div data-id="element-2493">
                <label htmlFor="topic" className={labelClass} data-id="element-2494">
                  What can we help with? *
                </label>
                <select id="topic" name="topic" required defaultValue="" className={inputClass} data-id="element-2495">
                  <option value="" disabled data-id="element-2496">
                    Select a topic…
                  </option>
                  <option value="RD Exam prep" data-id="element-2497">RD Exam prep</option>
                  <option value="CNSC Exam prep" data-id="element-2498">CNSC Exam prep</option>
                  <option value="Study access / account" data-id="element-2499">
                    Study access / account
                  </option>
                  <option value="Billing" data-id="element-2500">Billing</option>
                  <option value="General question" data-id="element-2501">General question</option>
                </select>
              </div>

              <div data-id="element-2502">
                <label htmlFor="message" className={labelClass} data-id="element-2503">
                  Message *
                </label>
                <textarea id="message" name="message" required rows={6} placeholder="Tell us how we can help…" className={`${inputClass} resize-none`} data-id="element-2504" />
              </div>

              {status === 'error' && <div className="flex items-start gap-3 rounded-xl bg-red-50 p-4" data-id="element-2505">
                  <AlertCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" data-id="element-2506" />
                  <p className="text-sm text-red-800" data-id="element-2507">{errorMessage}</p>
                </div>}

              <button type="submit" disabled={status === 'submitting'} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto" style={{
            backgroundColor: BLUE
          }} data-id="element-2508">
                {status === 'submitting' ? <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-2509" />
                    Sending…
                  </> : <>
                    Send message <ArrowRightIcon className="h-4 w-4" data-id="element-2510" />
                  </>}
              </button>

              <p className="pt-1 text-xs text-[#94A3B8]" data-id="element-2511">
                Your information stays private. We respond personally to every
                message.
              </p>
            </form>}
        </div>

        <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-[#94A3B8]" data-id="element-2512">
          Prefer to browse first?{' '}
          <Link to="/cnsc-prep" className="font-semibold" style={{
          color: BLUE
        }} data-id="element-2513">
            Explore RD Ready study tools
          </Link>
          .
        </p>
      </section>
    </div>;
}